import { last } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { useRequest } from "@prismicio/editor-support/Suspense"
import { useState } from "react"
import { z } from "zod"

import {
  type AuthStrategy,
  throwConfigError,
  useEditorConfig
} from "../../EditorConfig"

/* === Search catalog === */

interface IntegrationCatalogSearchArgs {
  searchTerm: string
  catalogId: string
}

export function useIntegrationCatalogSearch(
  args: IntegrationCatalogSearchArgs
) {
  const { searchTerm, catalogId } = args

  const config = useEditorConfig()

  if (!config.integrationFields) throwConfigError("integrationFields")

  const { integrationFieldsApiBaseUrl, repository, authStrategy } = config

  const [page, setPage] = useState(1)
  const [items, setItems] = useState<IntegrationItem[]>([])
  const [previousSearchTerm, setPreviousSearchTerm] = useState("")

  const response = useRequest(searchIntegrationCatalog, [
    integrationFieldsApiBaseUrl,
    authStrategy,
    repository,
    catalogId,
    searchTerm,
    searchTerm === previousSearchTerm ? page : 1 // Reset the page immediately if the search team changes
  ])

  if (searchTerm !== previousSearchTerm) {
    // The search term or filters have changed, so the state should reset to avoid
    // duplicate items in the list.
    setPreviousSearchTerm(searchTerm)
    setPage(1)
    setItems(response.items)
  } else if (
    response.items.length > 0 &&
    last(items)?.id !== last(response.items)?.id
  ) {
    setItems((currItems) => currItems.concat(response.items))
  }

  const hasNextPage = page < response.totalPages

  function fetchNextPage() {
    if (hasNextPage) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    items,
    response,
    fetchNextPage,
    hasNextPage
  }
}

function searchIntegrationCatalog(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  repository: string,
  catalogId: string,
  searchTerm: string,
  page = 1,
  limit = 30
) {
  const requestUrl = new URL(`if/search/${catalogId}`, baseUrl)
  requestUrl.searchParams.set("searchTerm", searchTerm)
  requestUrl.searchParams.set("limit", limit.toString())
  requestUrl.searchParams.set("page", page.toString())

  return request(requestUrl, {
    headers: {
      repository
    },
    credentials: getRequestCredentials(authStrategy),
    okType: integrationCatalogSearchResponse
  })
}

const integrationItem = z.object({
  id: z.string(),
  title: z.string(),
  imageURL: z.string().optional().nullable()
})

const integrationCatalogSearchResponse = z.object({
  items: z.array(integrationItem),
  totalPages: z.number(),
  page: z.number()
})

export type IntegrationItem = z.TypeOf<typeof integrationItem>

/* === Read catalog === */

interface IntegrationCatalogReadArgs {
  catalogId: string
  itemId: string
}

export function useIntegrationCatalogRead(args: IntegrationCatalogReadArgs) {
  const { itemId, catalogId } = args

  const config = useEditorConfig()

  if (!config.integrationFields) throwConfigError("integrationFields")

  const { integrationFieldsApiBaseUrl, repository, authStrategy } = config

  const response = useRequest(readIntegrationCatalog, [
    integrationFieldsApiBaseUrl,
    authStrategy,
    repository,
    catalogId,
    itemId
  ])

  const item = response.items[0]

  if (!item) throw new Error("Item not found")

  return {
    item
  }
}

function readIntegrationCatalog(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  repository: string,
  catalogId: string,
  itemId: string
) {
  const requestUrl = new URL(`if/read/${catalogId}`, baseUrl)
  requestUrl.searchParams.set("ids", [itemId].toString()) // We won't need multiple items based on ids [...]

  return request(requestUrl, {
    headers: {
      repository
    },
    credentials: getRequestCredentials(authStrategy),
    okType: integrationCatalogReadResponse
  })
}

const integrationCatalogReadResponse = z.object({
  items: z.array(integrationItem)
})
